import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { Fragment, useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

import Modal from "@/components/Modal"
import SkeletonRow from "@/components/SkeletonRow"
import { Badge } from "@/components/ui/badge"
import useApiBaseUrl from "@/hooks/useApiBaseUrl"
import { useDebounce } from "@/hooks/useDebounce"
import { getListJalurJenis, getListUnitkerja } from "@/services/getData"
import { DataPendaftaran, JalurJenis, PaginationData, Unitkerja } from "@/types"
import { LuListFilter } from "react-icons/lu"
import { RiFileExcel2Fill } from "react-icons/ri"
import { useQuery } from "@tanstack/react-query"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import FormFilter from "./FormFilter"
import { Pagination } from "./Pagination"

interface DataTableProps<TData extends DataPendaftaran, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  paginationData: PaginationData<DataPendaftaran[]>
  paginationDataPendaftaranProfesi: PaginationData<DataPendaftaran[]>
  isPending: boolean
}

export function DataTable<TData extends DataPendaftaran, TValue>({
  columns,
  data,
  paginationData,
  isPending: isPendingDataPendaftaran,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState({})

  const { apiBaseUrl } = useApiBaseUrl()
  const [searchParams] = useSearchParams()
  const ids_ref_jalur_jenis = searchParams.get("ids_ref_jalur_jenis")
  const ids_uk = searchParams.get("ids_uk")

  const [showFilter, setShowFilter] = useState(false)
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") || ""
  )
  const debouncedQuery = useDebounce(searchQuery, 1000)
  const navigate = useNavigate()
  const location = useLocation()

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  })

  const { data: listJalurJenis } = useQuery({
    queryKey: ["listJalurJenis"],
    queryFn: getListJalurJenis,
  })
  const ids_ref_jalur_jenis_array = ids_ref_jalur_jenis?.split("-")
  const listJalurJenisAktifFilter =
    listJalurJenis?.filter((item: JalurJenis) => {
      return ids_ref_jalur_jenis_array?.includes(
        String(item.id_ref_jalur_jenis)
      )
    }) || []

  const { data: listUnitkerja } = useQuery({
    queryKey: ["listUnitkerja"],
    queryFn: getListUnitkerja,
  })
  const listProdi = listUnitkerja?.filter((unitkerja: Unitkerja) => {
    return unitkerja.unitkerja_group_key == "5" && unitkerja.is_active
  })
  const ids_uk_array = ids_uk?.split("-")
  const listProdiAktifFilter =
    listProdi?.filter((item: Unitkerja) => {
      return ids_uk_array?.includes(String(item.uk_id))
    }) || []

  useEffect(() => {
    const params = new URLSearchParams(location.search)

    if (debouncedQuery) {
      // if (params.get("search") !== debouncedQuery) {
      //   params.delete("page");
      // }

      if (
        params.get("search") !== debouncedQuery ||
        params.get("page") !== "1"
      ) {
        params.set("search", debouncedQuery)
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        })
      }
    } else {
      if (params.has("search") || params.get("page") !== "1") {
        params.delete("search")
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        })
      }
    }
  }, [debouncedQuery, navigate, location.pathname, location.search])

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 py-4">
        <div className="flex-1 flex items-center gap-3">
          <Input
            autoFocus
            placeholder="Filter by tahun, nomor pendaftaran, nama lengkap, status kelulusan, dan pilihan prodi..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="max-w-sm"
          />
          <Button
            variant={ids_ref_jalur_jenis ? "default" : "outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowFilter(true)}
          >
            Filter
            <LuListFilter />
          </Button>

          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => {
              const url = new URL(`${apiBaseUrl}/data-pendaftaran/export`)

              const params = new URLSearchParams(window.location.search)
              url.search = params.toString()

              window.open(url.toString(), "_blank")
            }}
          >
            Export
            <RiFileExcel2Fill />
          </Button>

          <div className="flex gap-1 flex-wrap">
            {listJalurJenisAktifFilter?.map((item: JalurJenis) => (
              <Badge variant={"outline"}>{item?.nama_jalur_jenis}</Badge>
            ))}
            {listProdiAktifFilter?.map((item: Unitkerja) => (
              <Badge variant={"outline"}>{item?.nama}</Badge>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  )
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {isPendingDataPendaftaran ? (
        <div className="flex flex-col gap-y-3">
          {Array(10)
            .fill(null)
            .map((_, index) => {
              return <SkeletonRow key={index} />
            })}
        </div>
      ) : (
        <div className="border rounded-xl">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => {
                return (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => {
                      return (
                        <>
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                          {index === 0 && <TableHead></TableHead>}
                        </>
                      )
                    })}
                    {[...Array(4)].map((_, index) => {
                      return (
                        <Fragment key={index}>
                          <TableHead>Pilihan ke-{index + 1}</TableHead>
                        </Fragment>
                      )
                    })}
                  </TableRow>
                )
              })}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <>
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      </>
                    ))}
                    {[...Array(4)].map((_, index) => {
                      const pilihanProdi =
                        row?.original.data_pilihan_prodi?.find(
                          (prodi) => prodi.urutan_program_studi === index + 1
                        )

                      return (
                        <Fragment key={index}>
                          <TableCell>
                            {pilihanProdi ? (
                              <Badge
                                variant={
                                  pilihanProdi?.is_lulus ? "default" : "dark"
                                }
                                className="whitespace-nowrap"
                              >
                                {pilihanProdi?.unitkerja?.nama}
                              </Badge>
                            ) : (
                              <Badge variant={"outline"}>Kosong</Badge>
                            )}
                          </TableCell>
                        </Fragment>
                      )
                    })}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <br />
      <div>
        <Pagination table={table} paginationData={paginationData} />
      </div>

      <Modal show={showFilter} onClose={() => setShowFilter(false)} width="2xl">
        <FormFilter closeModal={() => setShowFilter(false)} />
      </Modal>
    </div>
  )
}
